import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from 'react-dom';
import { MotionValue, m } from 'framer-motion';

const ScrollBar = ({
  zIndex = 102,
  y,
  mountTo,
  offsetY = 0,
  delay = 300,
}: {
  zIndex?: number;
  y?: MotionValue<number>;
  mountTo?: HTMLElement;
  offsetY?: number;
  delay?: number;
}) => {
  const TRACK_HEIGHT = 80;
  const ref = useRef<HTMLDivElement>(null);
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const [thumb, setThumb] = useState({ height: 0, top: 0 });
  const [isVisible, setIsVisible] = useState(false);
  // 시트 안쪽 끝에서 스크롤바까지의 거리(px). 시트는 화면에 fixed 로 붙지만 가로 모드에선 좌우 15vw 를 비우고 가운데 놓이므로
  // 화면 끝이 아니라 시트 끝을 기준으로 잡아야 시트 밖(화면 우상단)에 뜨지 않는다.
  const [edgeInset, setEdgeInset] = useState(0);
  const isRTL = BResource.getSelectedLocale() === 'ar';

  const updateEdgeInset = useCallback(() => {
    if (!containerRef) return;
    const rect = containerRef.getBoundingClientRect();
    setEdgeInset(isRTL ? rect.left : window.innerWidth - rect.right);
  }, [containerRef, isRTL]);

  const onScroll = useCallback(() => {
    if (!containerRef) return;
    const { scrollHeight, clientHeight, scrollTop } = containerRef;
    const thumbHeight = (clientHeight / scrollHeight) * TRACK_HEIGHT;
    const maxScrollTop = scrollHeight - clientHeight;
    const maxThumbTop = TRACK_HEIGHT - thumbHeight;

    const thumbTop = (scrollTop / maxScrollTop) * maxThumbTop;

    setThumb({ height: thumbHeight, top: thumbTop });

    const iconContainer = document.querySelector('[data-component="ProfileImage"] .icon-group-cont') as HTMLElement;
    if (iconContainer) {
      iconContainer.style.insetInlineEnd = thumbHeight === TRACK_HEIGHT ? '0' : '1rem';
    }
    
  }, [containerRef]);

  // transition 완료 후 렌더링
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  useEffect(() => {
    if (!isVisible || !ref.current) return;
    const container = ref.current.closest('.scroll-container');
    if (!container) return;
    setContainerRef(container as HTMLDivElement);
  }, [isVisible]);

  useEffect(() => {
    if (containerRef) {
      onScroll();
    }
  }, [containerRef, onScroll]);

  useEffect(() => {
    if (!containerRef) return;

    onScroll();
    updateEdgeInset();

    containerRef.addEventListener('scroll', onScroll);
    // 회전으로 시트 폭·위치가 바뀌면 끝 기준도 다시 잡는다. window resize 시점엔 아직 body 의 landscape 클래스가
    // 적용되기 전일 수 있어, 시트 자체의 크기 변화를 관찰한다.
    const resizeObserver = new ResizeObserver(updateEdgeInset);
    resizeObserver.observe(containerRef);

    return () => {
      containerRef.removeEventListener('scroll', onScroll);
      resizeObserver.disconnect();
    };
  }, [containerRef, onScroll, updateEdgeInset]);

  const content = (
    <m.div
      ref={ref}
      data-component="ScrollBar"
      className="fixed h-[80px] w-2 rounded-full"
      style={{
        direction: isRTL ? 'rtl' : 'ltr',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex,
        y,
        top: `calc(${offsetY}px + 1rem)`,
        insetInlineEnd: `calc(${edgeInset}px + 1rem)`,
      }}
    >
      <div className="absolute left-0 right-0 top-0 rounded-full bg-white" style={{ height: `${thumb.height}px`, top: `${thumb.top}px` }} />
    </m.div>
  );

  if (!isVisible) return null;

  if (thumb.height === TRACK_HEIGHT) return null;

  if (containerRef) {
    return createPortal(content, mountTo ?? document.getElementById('hMainContainer') ?? document.body);
  }

  return content;
};

export default ScrollBar;